import "./homepage.css";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import Navbar from "../components/navbar";
import SiteFooter from "../components/siteFooter";

function DifficultyPill({ difficulty }) {
  const tone = difficulty === "Easy" ? "easy" : difficulty === "Medium" ? "medium" : "hard";
  return (
    <span className={`home__pill home__pill--${tone}`} aria-label={`Difficulty: ${difficulty}`}>
      {difficulty}
    </span>
  );
}

export default function Homepage() {
  const navigate = useNavigate();

  const problems = useMemo(
    () => [
      {
        id: "boggle",
        title: "Boggle",
        concepts: "String Manipulation",
        difficulty: "Hard",
        summary: "Grid search + constraints. Great for clarifying requirements and edge cases.",
      },
      {
        id: "bananas",
        title: "Bananas",
        concepts: "Arrays",
        difficulty: "Medium",
        summary: "Array reasoning and clean iteration. Great for communication and correctness.",
      },
      {
        id: "rail_tickets",
        title: "Rail Tickets",
        concepts: "Dynamic Programming",
        difficulty: "Hard",
        summary: "DP tradeoffs and state definition. Great for explaining complexity and optimizations.",
      },
    ],
    [],
  );

  const interviewFlow = useMemo(
    () => [
      {
        phase: "Phase 1",
        title: "Clarifying Questions",
        text: "You ask questions to clarify constraints, inputs/outputs, and edge cases before proposing a solution.",
      },
      {
        phase: "Phase 2",
        title: "Guiding Questions",
        text: "The AI interviewer probes your understanding. If you ask for help, you’ll get at most one hint at a time.",
      },
      {
        phase: "Phase 3",
        title: "Implementation",
        text: "Write code (or pseudocode) and iterate. Expect feedback and small breaking test cases—not a full solution dump.",
      },
      {
        phase: "Phase 4",
        title: "Test Cases",
        text: "You suggest what to test. Provided test cases aren’t revealed until you’re done suggesting your own.",
      },
      {
        phase: "Phase 5",
        title: "Evaluation",
        text: "You get quantitative scoring (questions asked, guiding accuracy, hints, missed tests) plus narrative feedback.",
      },
      {
        phase: "Phase 6",
        title: "Conclusion",
        text: "The exercise ends cleanly so you can reset and run another mock with a fresh problem.",
      },
    ],
    [],
  );

  return (
    <div className="home">
      <Navbar />

      <main className="home__main">
        <section className="home__hero" aria-label="Landing hero">
          <div className="home__heroLeft">
            <h1 className="home__headline">Free AI mock interviews</h1>
            <p className="home__subhead">
              Talk through a LeetCode-style problem with an AI interviewer that prompts, probes, and
              keeps you honest about tradeoffs.
            </p>

            <div className="home__ctaRow">
              <button
                className="home__ctaPrimary"
                type="button"
                onClick={() =>
                  document.getElementById("problems")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                See the top questions
              </button>
              <button
                className="home__ctaSecondary home__ctaSecondary--white"
                type="button"
                onClick={() => navigate("/select-problem")}
              >
                View all questions
              </button>
            </div>

            <div className="home__fineprint">
              <strong>Free</strong>, <strong>ad-supported</strong>, no signup. Just pick a problem
              and start practicing!
            </div>
          </div>

        </section>

        <section className="home__section" id="how" aria-label="How it works">
          <h2 className="home__sectionTitle">How it works</h2>
          <div className="home__steps">
            <div className="home__step">
              <div className="home__stepNum">1</div>
              <div className="home__stepBody">
                <div className="home__stepTitle">Choose a Problem</div>
                <div className="home__stepText">Choose one of our LeetCode-style questions.</div>
              </div>
            </div>
            <div className="home__step">
              <div className="home__stepNum">2</div>
              <div className="home__stepBody">
                <div className="home__stepTitle">Explain Yourself</div>
                <div className="home__stepText">
                  The AI asks clarifying questions and pushes for correctness and tradeoffs.
                </div>
              </div>
            </div>
            <div className="home__step">
              <div className="home__stepNum">3</div>
              <div className="home__stepBody">
                <div className="home__stepTitle">Get Feedback</div>
                <div className="home__stepText">
                  Get probing follow-ups on complexity, edge cases, testing, and tradeoffs.
                </div>
              </div>
            </div>
            <div className="home__step">
              <div className="home__stepNum">4</div>
              <div className="home__stepBody">
                <div className="home__stepTitle">Give Back</div>
                <div className="home__stepText">
                  Have a great interview question? Add it on{" "}
                  <a className="home__inlineLink" href="https://github.com/ace-n" target="_blank" rel="noreferrer">
                    GitHub
                  </a>!
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="home__section home__section--fullBleed" id="flow" aria-label="Interview flow">
          <div className="home__sectionHeaderRow">
            <h2 className="home__sectionTitle">Interview flow</h2>
          </div>

          <div className="home__flowList" role="list" aria-label="Interview phases">
            {interviewFlow.map((item, idx) => (
              <div className="home__flowNode" key={item.phase} role="listitem">
                <div className="home__flowCard">
                  <div className="home__flowTop">
                    <div className="home__flowTitle">{item.title}</div>
                    <div className="home__flowPhase">{item.phase}</div>
                  </div>
                  <div className="home__flowText">{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="home__section" id="problems" aria-label="Problems">
          <div className="home__sectionHeaderRow">
            <h2 className="home__sectionTitle">Top questions</h2>
            <div className="home__sectionHint">Click one to get started!</div>
          </div>

          <div className="home__problemGrid">
            {problems.map((p) => (
              <button
                key={p.id}
                className="home__problemCard"
                type="button"
                onClick={() => navigate(`/chat/${p.id}`)}
              >
                <div className="home__problemTop">
                  <div className="home__problemTitle">{p.title}</div>
                  <DifficultyPill difficulty={p.difficulty} />
                </div>
                <div className="home__problemMeta">{p.concepts}</div>
                <div className="home__problemAction">
                  <span>Try this problem</span>
                  <span aria-hidden="true">→</span>
                </div>
              </button>
            ))}
          </div>

          <div className="home__problemsCta">
            <button
              className="home__ctaSecondary home__ctaSecondary--white"
              type="button"
              onClick={() => navigate("/select-problem")}
            >
              View all questions
            </button>
          </div>
        </section>

        <SiteFooter title="Blåhaj" text="Free AI mock interview practice." />
      </main>
    </div>
  );
}

